/* eslint-disable no-param-reassign */
import { createSlice, createSelector } from '@reduxjs/toolkit';

const initialState = {
  currentContact: null,
  messages: {},
  uiState: {},
  recentMaxIdsFetched: {},
  allMessagesLoaded: {},
};

const nfChatSlice = createSlice({
  name: 'nfChat',
  initialState,
  reducers: {
    setCurrentContact: (state, action) => {
      state.currentContact = action.payload.contact;
    },

    appendMessagesForContact(state, action) {
      const { newMessages, contactId } = action.payload;

      if (!newMessages || newMessages.length === 0) {
        // if we have no new messages, but have loaded messages
        // previously, we can assume that we have loaded all messages
        if (state.recentMaxIdsFetched[contactId].ids.length > 0) {
          state.allMessagesLoaded[contactId] = true;
        }

        return;
      }

      if (!state.messages[contactId]) {
        state.messages[contactId] = { allMessages: [] };
      }

      if (!state.recentMaxIdsFetched[contactId]) {
        state.recentMaxIdsFetched[contactId] = { ids: [] };
      }

      // reverse the order of the latest page of messages,
      // and prepend them to the existing messages collection
      state.messages[contactId].allMessages.unshift(
        ...newMessages.toSorted((a, b) => a.id - b.id),
      );

      state.recentMaxIdsFetched[contactId].ids.push(
        newMessages[newMessages.length - 1].id,
      );
    },

    setInitialRenderForContact(state, action) {
      const { contactId, initialRender } = action.payload;

      if (!state.uiState[contactId]) {
        state.uiState[contactId] = { initialRender: true };
      }

      state.uiState[contactId].initialRender = initialRender;
    },
  },
});

//
// ********************* SELECTORS ********************* //
//

const messagesSelector = (state) => state.nfChat.messages;
const uiStateSelector = (state) => state.nfChat.uiState;
const recentMaxIdsFetchedSelector = (state) => state.nfChat.recentMaxIdsFetched;
const allMessagesLoadedSelector = (state) => state.nfChat.allMessagesLoaded;

const selectors = {
  currentContactSelector: (state) => state.nfChat.currentContact,

  allMessagesLoadedForContactSelector: createSelector(
    [allMessagesLoadedSelector, (state, contactId) => contactId],
    (allMessagesLoaded, contactId) => {
      if (!contactId || !allMessagesLoaded[contactId]) return null;
      return allMessagesLoaded[contactId];
    },
  ),

  messagesForContactSelector: createSelector(
    [messagesSelector, (state, contactId) => contactId],
    (messages, contactId) => {
      if (!contactId) return null;
      if (!messages[contactId]) return [];
      return messages[contactId].allMessages;
    },
  ),

  uiStateForContactSelector: createSelector(
    [uiStateSelector, (state, contactId) => contactId],
    (uiState, contactId) => {
      if (!contactId) return null;
      if (!uiState[contactId]) return { initialRender: true };

      return uiState[contactId];
    },
  ),

  recentMaxIdForContactSelector: createSelector(
    [recentMaxIdsFetchedSelector, (state, contactId) => contactId],
    (recentMaxIdsFetched, contactId) => {
      if (!contactId) return null;
      if (!recentMaxIdsFetched[contactId]) return null;

      const maxIds = recentMaxIdsFetched[contactId].ids;

      if (maxIds.length === 0) return null;

      // return the most recent maxId
      return maxIds[maxIds.length - 1];
    },
  ),
};

export default nfChatSlice;

export const {
  setCurrentContact,
  appendMessagesForContact,
  setInitialRenderForContact,
  appendMaxIdFetchedForContact,
} = nfChatSlice.actions;

export const {
  currentContactSelector,
  recentMaxIdForContactSelector,
  messagesForContactSelector,
  uiStateForContactSelector,
  allMessagesLoadedForContactSelector,
} = selectors;
